type UnlayerPollValues = {
  pollId?: string;
  question?: string;
  pollOptions?: string;
  alignment?: string;
  buttonColor?: string;
  buttonTextColor?: string;
  questionColor?: string;
  buttonPadding?: string;
  buttonBorderRadius?: string;
};

type UnlayerPollOptionValue = {
  id: string;
  text: string;
};

type UnlayerPollWidget = "alignment" | "color_picker" | "poll_choices" | "text";

type UnlayerPollOption = {
  label: string;
  defaultValue: string;
  widget: UnlayerPollWidget;
};

type UnlayerPollOptionGroup = {
  title: string;
  position: number;
  collapsed: boolean;
  options: Record<string, UnlayerPollOption>;
};

type UnlayerPollToolDefinition = {
  name: string;
  label: string;
  icon: string;
  pollResponseUrlPrefix: string;
  supportedDisplayModes: ["email"];
  options: Record<string, UnlayerPollOptionGroup>;
  values: UnlayerPollValues;
};

type UnlayerCustomToolHost = {
  createViewer?: (viewer: { render: (values: UnlayerPollValues) => string }) => unknown;
  registerPropertyEditor?: (propertyEditor: unknown) => void;
  registerTool: (tool: unknown) => void;
  React?: typeof import("react");
};

type PollCustomToolConfig = {
  pollResponseUrlPrefix: string;
};

const DEFAULT_POLL_OPTIONS = '[{"id":"","text":""}]';

export function makePollCustomTool(config: PollCustomToolConfig): string {
  return makeUnlayerCustomToolScript(registerPollCustomTool, {
    name: "poll",
    label: "Poll",
    icon: "fa-bar-chart",
    pollResponseUrlPrefix: config.pollResponseUrlPrefix,
    supportedDisplayModes: ["email"],
    options: {
      content: {
        title: "Poll",
        position: 1,
        collapsed: false,
        options: {
          question: {
            label: "Question",
            defaultValue: "What should we do next?",
            widget: "text",
          },
          pollOptions: {
            label: "Choices",
            defaultValue: DEFAULT_POLL_OPTIONS,
            widget: "poll_choices",
          },
        },
      },
      style: {
        title: "Button Colors",
        position: 2,
        collapsed: false,
        options: {
          buttonColor: {
            label: "Button color",
            defaultValue: "#185abc",
            widget: "color_picker",
          },
          buttonTextColor: {
            label: "Button text color",
            defaultValue: "#ffffff",
            widget: "color_picker",
          },
        },
      },
      layout: {
        title: "Layout",
        position: 3,
        collapsed: false,
        options: {
          alignment: {
            label: "Alignment",
            defaultValue: "center",
            widget: "alignment",
          },
          questionColor: {
            label: "Question color",
            defaultValue: "#111827",
            widget: "color_picker",
          },
          buttonPadding: {
            label: "Button padding",
            defaultValue: "12px 18px",
            widget: "text",
          },
          buttonBorderRadius: {
            label: "Button radius",
            defaultValue: "6px",
            widget: "text",
          },
        },
      },
    },
    values: {
      pollId: "",
      question: "What should we do next?",
      pollOptions: DEFAULT_POLL_OPTIONS,
      alignment: "center",
      buttonColor: "#185abc",
      buttonTextColor: "#ffffff",
      questionColor: "#111827",
      buttonPadding: "12px 18px",
      buttonBorderRadius: "6px",
    },
  });
}

function makeUnlayerCustomToolScript(
  registerTool: (definition: UnlayerPollToolDefinition) => void,
  definition: UnlayerPollToolDefinition,
): string {
  return `(${registerTool.toString()})(${JSON.stringify(definition)});`;
}

function registerPollCustomTool(definition: UnlayerPollToolDefinition): void {
  const unlayer = (globalThis as unknown as { unlayer?: UnlayerCustomToolHost }).unlayer;
  if (!unlayer) return;
  const unlayerHost = unlayer;
  const React = unlayerHost.React;
  const { pollResponseUrlPrefix, ...toolDefinition } = definition;

  function escapeHtml(value: string | undefined): string {
    if (!value) return "";
    return value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function makePollOption(text: string): UnlayerPollOptionValue {
    return {
      id: crypto.randomUUID(),
      text,
    };
  }

  function makeDefaultPollOptions(): UnlayerPollOptionValue[] {
    return [{ id: "", text: "" }];
  }

  function normalizePollOptions(values: UnlayerPollValues): UnlayerPollOptionValue[] {
    if (!values.pollOptions) return makeDefaultPollOptions();

    try {
      const options = JSON.parse(values.pollOptions) as unknown;
      if (!Array.isArray(options) || options.length === 0) {
        return makeDefaultPollOptions();
      }

      return options as UnlayerPollOptionValue[];
    } catch {
      return makeDefaultPollOptions();
    }
  }

  function registerPollChoicesEditor(): void {
    if (!React || typeof unlayerHost.registerPropertyEditor !== "function") return;
    const ReactHost = React;

    function PollChoicesEditor(props: {
      value?: string;
      onChange?: (value: string) => void;
      updateValue?: (value: string) => void;
    }): import("react").ReactNode {
      const values = normalizePollOptions({ pollOptions: props.value });
      const updateValue = (nextValue: UnlayerPollOptionValue[]): void => {
        const normalized = nextValue.length > 0 ? nextValue : [makePollOption("")];
        const serialized = JSON.stringify(normalized);
        props.updateValue?.(serialized);
        props.onChange?.(serialized);
      };

      return ReactHost.createElement(
        "div",
        { style: { display: "flex", flexDirection: "column", gap: 8 } },
        ...values.map((value, index) =>
          ReactHost.createElement(
            "div",
            {
              key: value.id || `choice-${index}`,
              style: { display: "flex", gap: 6, alignItems: "center" },
            },
            ReactHost.createElement("input", {
              "aria-label": `Choice ${index + 1}`,
              onChange: (event: { target: { value: string } }) => {
                const nextValue = values.slice();
                nextValue[index] = value.id
                  ? { ...value, text: event.target.value }
                  : makePollOption(event.target.value);
                updateValue(nextValue);
              },
              placeholder: `Choice ${index + 1}`,
              style: {
                border: "1px solid #d1d5db",
                borderRadius: 4,
                boxSizing: "border-box",
                flex: 1,
                fontSize: 13,
                minWidth: 0,
                padding: "8px 10px",
              },
              type: "text",
              value: value.text,
            }),
            ReactHost.createElement(
              "button",
              {
                "aria-label": `Remove choice ${index + 1}`,
                disabled: values.length === 1,
                onClick: () =>
                  updateValue(values.filter((_, removeIndex) => removeIndex !== index)),
                style: {
                  background: "transparent",
                  border: "1px solid #d1d5db",
                  borderRadius: 4,
                  color: values.length === 1 ? "#9ca3af" : "#374151",
                  cursor: values.length === 1 ? "not-allowed" : "pointer",
                  fontSize: 12,
                  padding: "8px 10px",
                },
                type: "button",
              },
              "Remove",
            ),
          ),
        ),
        ReactHost.createElement(
          "button",
          {
            onClick: () => updateValue([...values, makePollOption("")]),
            style: {
              background: "#ffffff",
              border: "1px solid #185abc",
              borderRadius: 4,
              color: "#185abc",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 700,
              padding: "8px 10px",
            },
            type: "button",
          },
          "Add another option",
        ),
      );
    }

    unlayerHost.registerPropertyEditor({
      name: "poll_choices",
      layout: "bottom",
      Widget: PollChoicesEditor,
    });
  }

  function renderButton(option: UnlayerPollOptionValue, values: UnlayerPollValues): string {
    if (!option.text) return "";

    const style = [
      "display:inline-block",
      "box-sizing:border-box",
      "min-width:120px",
      "margin:4px",
      `padding:${escapeHtml(values.buttonPadding || "12px 18px")}`,
      `border-radius:${escapeHtml(values.buttonBorderRadius || "6px")}`,
      `background:${escapeHtml(values.buttonColor || "#185abc")}`,
      `color:${escapeHtml(values.buttonTextColor || "#ffffff")}`,
      "font-family:Arial, sans-serif",
      "font-size:14px",
      "font-weight:700",
      "line-height:18px",
      "text-align:center",
      "text-decoration:none",
    ].join(";");

    const href = `${pollResponseUrlPrefix}${option.id}`;

    return `<a href="${escapeHtml(href)}" style="${style}">${escapeHtml(option.text)}</a>`;
  }

  function renderPoll(values: UnlayerPollValues): string {
    const options = normalizePollOptions(values).filter((option) => option.text.trim().length > 0);
    const align = escapeHtml(values.alignment || "center");
    const buttons = options.map((option) => renderButton(option, values)).join("");

    return [
      `<div style="padding:20px;text-align:${align};font-family:Arial, sans-serif;">`,
      `<div style="margin-bottom:12px;color:${escapeHtml(values.questionColor || "#111827")};font-size:18px;font-weight:700;line-height:24px;">`,
      escapeHtml(values.question || "What should we do next?"),
      "</div>",
      `<div style="text-align:${align};">`,
      buttons,
      "</div>",
      "</div>",
    ].join("");
  }

  registerPollChoicesEditor();

  const renderer: {
    Viewer?: unknown;
    exporters: {
      email: (values: UnlayerPollValues) => string;
      web: (values: UnlayerPollValues) => string;
    };
    head: { css: () => string; js: () => string };
  } = {
    exporters: {
      email: renderPoll,
      web: renderPoll,
    },
    head: {
      css: () => "",
      js: () => "",
    },
  };

  if (typeof unlayerHost.createViewer === "function") {
    renderer.Viewer = unlayerHost.createViewer({
      render: renderPoll,
    });
  }

  unlayerHost.registerTool({
    ...toolDefinition,
    renderer,
    validator: () => [],
  });
}
